import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { startTransition, useMemo } from 'react';
import { toast } from 'sonner';
import { XIcon } from 'lucide-react';
import { useMutation, useReadQuery, useSuspenseQuery } from '@apollo/client/react';

import { Button } from '@/components/ui/button';
import { AppMembershipRemoveDocument } from 'src/graphql/actions/appMemberships/appMembershipRemove.mutation';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { UserRoleEnum } from 'src/graphql/schema';
import { Card } from '@/components/ui/card';

import { getMembershipSortPriority } from '../lib';
import { refetchAppMemberships } from '../api';
import { useAppMembershipsManagement } from '../model';

import RoleCell from './RoleCell';
import PendingRoleCell from './PendingRoleCell';
import PermissionTooltip from './PermissionTooltip';

type IMembershipRow = {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRoleEnum;
  };
  role: string;
  isPending: boolean;
  action: {
    canUpdate: boolean;
    canRemove: boolean;
  };
};

const RemoveCell = ({ id, canRemove }: { id: string; canRemove: boolean }) => {
  const onCompleted = () => startTransition(refetchAppMemberships);

  const [removeAppMembership, { loading }] = useMutation(AppMembershipRemoveDocument, {
    onCompleted,
  });

  const onRemove = async () => {
    try {
      await removeAppMembership({
        variables: {
          appMembershipId: id,
        },
      });
      toast.success('Member access has been removed');
    } catch (error) {
      toast.error('Failed to remove member access');
    }
  };

  if (canRemove) {
    return (
      <Button variant="ghost" onClick={onRemove} disabled={loading} isLoading={loading} size="icon" className="w-7 h-7">
        <XIcon />
      </Button>
    );
  }

  return null;
};

const PendingRemoveCell = ({ id }: { id: string }) => {
  const { removePendingMember } = useAppMembershipsManagement();

  const onRemove = () => removePendingMember(id);

  return (
    <Button variant="ghost" onClick={onRemove} size="icon" className="w-7 h-7">
      <XIcon />
    </Button>
  );
};

const columnHelper = createColumnHelper<IMembershipRow>();
const columns = [
  columnHelper.accessor('user', {
    cell: (props) => {
      const { name, email } = props.getValue();
      const fallback = name.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-7 rounded-full">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <div className="grow min-w-0">
            <div className="text-sm text-foreground font-semibold truncate">{name}</div>
            <div className="text-xs text-muted-foreground truncate">{email}</div>
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    cell: (props) => (
      <div className="flex justify-end">
        {props.row.original.isPending ? (
          <PendingRoleCell role={props.getValue()} id={props.row.original.id} />
        ) : (
          <RoleCell
            role={props.getValue()}
            id={props.row.original.id}
            canUpdate={props.row.original.action.canUpdate}
            userRole={props.row.original.user.role}
          />
        )}
      </div>
    ),
  }),
  columnHelper.accessor('action', {
    cell: (props) =>
      props.row.original.isPending ? (
        <PendingRemoveCell id={props.row.original.id} />
      ) : (
        <RemoveCell id={props.row.original.id} canRemove={props.row.original.action.canRemove} />
      ),
  }),
];

const AddedMemberships = () => {
  const { queryAppMembershipsRef, pendingMembers } = useAppMembershipsManagement();
  const {
    data: { appMemberships },
  } = useReadQuery(queryAppMembershipsRef);
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);

  const membersMapped = useMemo(() => {
    const sortedNodes = appMemberships.nodes.slice().sort((a, b) => {
      const priorityA = getMembershipSortPriority(a.user, profile.id);
      const priorityB = getMembershipSortPriority(b.user, profile.id);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    const mappedNodes = sortedNodes.map((membership) => ({
      id: membership.id,
      user: {
        id: membership.user.id,
        name: membership.user.name || '-',
        email: membership.user.email || '-',
        role: membership.user.role,
      },
      role: membership.role,
      isPending: false,
      action: {
        canUpdate: membership.policy.update,
        canRemove: membership.policy.remove,
      },
    }));

    const mappedPendingMembers = pendingMembers.map((member) => ({
      id: member.id,
      user: {
        id: member.id,
        name: member.name || '-',
        email: member.email || '-',
        role: UserRoleEnum.User,
      },
      role: member.role,
      isPending: true,
      action: {
        canUpdate: true,
        canRemove: true,
      },
    }));

    return [...mappedNodes, ...mappedPendingMembers];
  }, [appMemberships.nodes, profile.id, pendingMembers]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-2.5 overflow-hidden flex flex-col py-3.5 px-4">
      <h2 className="text-sm text-foreground">
        Application members ({appMemberships.nodesCount + pendingMembers.length})
      </h2>
      <Card className="rounded-md overflow-hidden grow">
        <Table maxHeight="max-h-[320px]" className="table-fixed">
          <colgroup>
            <col />
            <col className="w-30" />
            <col className="w-16" />
          </colgroup>
          <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:left-0 after:bottom-0 after:w-full after:border-b after:border-border">
            <TableRow className="hover:bg-white">
              <TableHead className="h-8 text-xs">Team member</TableHead>
              <TableHead colSpan={2} className="h-8 text-xs text-right">
                <div className="flex justify-end items-center gap-2">
                  Permissions <PermissionTooltip />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const [userCell, roleCell, actionCell] = row.getVisibleCells();
              const isOwner = row.original.user.role === UserRoleEnum.Owner;

              return (
                <TableRow key={row.id} className="hover:bg-white">
                  <TableCell key={userCell.id} className="py-2.25">
                    {flexRender(userCell.column.columnDef.cell, userCell.getContext())}
                  </TableCell>
                  <TableCell key={roleCell.id} colSpan={isOwner ? 2 : 1} className="py-2.25">
                    {flexRender(roleCell.column.columnDef.cell, roleCell.getContext())}
                  </TableCell>
                  {!isOwner && (
                    <TableCell key={actionCell.id} className="py-2.25">
                      {flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default withSuspense(AddedMemberships);
